import {Injectable, NgZone} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, throwError, timer} from 'rxjs';
import {filter, mergeMap, retryWhen, tap} from "rxjs/operators";

import {CUSTOM_URL_SCHEME, SERVER_INFO, SYSTEM_INFO_API_URL} from '../../defines/protocol';
import {APP_VERSION, INAPPBROWSER_CLOSE_BUTTON_CAPTION, INAPPBROWSER_TOOLBAR_COLOR} from '../../defines/client.const';
import {CmpVersion} from '../../defines/common.func';
import {CordovaService} from '../cordova/cordova.service';
import {FcmService} from '../fcm/fcm.service';
import {ApiService} from '../api/api.service';
import {UserService} from '../user/user.service';
import {PLATFORM_TYPE} from "../cordova/cordova.define";
import {NotiService} from "../noti/noti.service";
import {environment} from "../../../../environments/environment";
import {LayoutMgrService} from '../layout-mgr/layout-mgr.service';
import {MP_RESULT, MpConfig} from '../../components/msg-popup/msg-popup.component';

// https://www.theiphonewiki.com/wiki/Models
const iPhoneModelWithoutNotch: Array<string> = [
  // 'iPhone15,3',                           // iPhone 14 Pro Max
  // 'iPhone15,2',                           // iPhone 14 Pro
  // 'iPhone14,8',                           // iPhone 14 Plus
  // 'iPhone14,7',                           // iPhone 14
  'iPhone14,6',                           // iPhone SE 3rd
  // 'iPhone14,3',                           // iPhone 13 Pro Max
  // 'iPhone14,2',                           // iPhone 13 Pro
  // 'iPhone14,5',                           // iPhone 13
  // 'iPhone14,4',                           // iPhone 13 mini
  // 'iPhone13,4',                           // iPhone 12 Pro Max
  // 'iPhone13,3',                           // iPhone 12 Pro
  // 'iPhone13,2',                           // iPhone 12
  // 'iPhone13,1',                           // iPhone 12 mini
  'iPhone12,8',                           // iPhone SE 2nd
  // 'iPhone12,5',                           // iPhone 11 Pro Max
  // 'iPhone12,3',                           // iPhone 11 Pro
  // 'iPhone12,1',                           // iPhone 11
  // 'iPhone11,8',                           // iPhone XR
  // 'iPhone11,6',                           // iPhone XS Max
  // 'iPhone11,2',                           // iPhone XS
  // 'iPhone10,6', 'iPhone10,3',             // iPhone X
  'iPhone10,5', 'iPhone10,2',             // iPhone 8 Plus
  'iPhone10,4', 'iPhone10,1',             // iPhone 8
  'iPhone9,4', 'iPhone9,2',               // iPhone 7 Plus
  'iPhone9,3', 'iPhone9,1',               // iPhone 7
  'iPhone8,4',                            // iPhone SE
  'iPhone8,2',                            // iPhone 6s Plus
  'iPhone8,1',                            // iPhone 6s
  'iPhone7,1',                            // iPhone 6 Plus
  'iPhone7,2',                            // iPhone 6
  'iPhone6,2', 'iPhone6,1',               // iPhone 5s
  'iPhone5,4', 'iPhone5,3',               // iPhone 5c
  'iPhone5,2', 'iPhone5,1',               // iPhone 5
  'iPhone4,1',                            // iPhone 4S
  'iPhone3,3', 'iPhone3,2', 'iPhone3,1',  // iPhone 4
  'iPhone2,1',                            // iPhone 3G
  'iPhone1,2', 'iPhone1,1',               // iPhone
];

function iPhoneModelWithNotch(model: string): boolean {
  return iPhoneModelWithoutNotch.indexOf(model) === -1;
}


/**
 * 파라미터가 있는 경우 queryParams 형식으로 파싱한다.
 */
function ParseRouteUrl(url: string): [string, {}|null] {
  const list = url.split('?');

  // 파라미터가 있는 경우 queryParams 형식으로 파싱한다.
  let queryParams = null;
  if (list.length > 1) {
    queryParams = {};
    const list2 = list[1].split('&');
    list2.forEach(item => {
      const list3 = item.split('=');
      if (list3.length > 1) {
        queryParams[list3[0]] = list3[1];
      }
    });
  }

  return [list[0], queryParams];
}


(window as any).handleOpenURL = (url: string) => {
  (window as any).handleOpenURL_LastURL = url;
};

// 로컬 스토리지 키 추가
export const LOCALSTORAGE_KEY_SOFT_UPDATE = 'soft_update_version';

// 소프트 업데이트 정보를 위한 인터페이스 추가
interface SoftUpdateInfo {
  version: string;
  date: number; // 타임스탬프로 저장
}

/**
 * (1) FCM에서 주소 받아서 분기하는 처리.
 * (2) Custom URL Scheme 에서 받은 값으로 분기하는 처리
 *
 * TODO: Login도 처리해야 함?
 */
@Injectable({
  providedIn: 'root'
})
export class SystemService {
  private _notch: boolean = null;
  // 소프트 업데이트 버전 정보 저장을 위한 변수 수정
  private shownSoftUpdateVersion: SoftUpdateInfo = null;

  public get notch(): boolean {
    return this._notch;
  }

  public get ios(): boolean {
    return this.cordovaSvc.isIOS;
  }


  public get android(): boolean {
    return this.cordovaSvc.isAndroid;
  }


  constructor(
    private zone: NgZone,
    private router: Router,
    private cordovaSvc: CordovaService,
    private api: ApiService,
    private user: UserService,
    private fcm: FcmService,
    private notiService: NotiService,
    private lm: LayoutMgrService,
  ) {
    cordovaSvc.deviceready.subscribe(() => {

      // TODO: 허가 받는 타이밍을 나중으로 조정하는 것을 고려할 것. 처음 부터 보이면 거부할 가능성이 높다.
      if (this.cordovaSvc.isIOS) {
        this.fcm.requestPermission().subscribe(() => {
          console.log('fcm.requestPermission() success');
        }, err => console.error(err));

        if (iPhoneModelWithNotch(device.model)) {
          this._notch = true;
        }

        // iOS 노치가 존재하는지 체크
        // this._notch = iPhoneModelWithNotch.indexOf(device.model) > -1;
      }

      const getAndSendToken = () => {
        this.fcm.getToken().subscribe(token => {
          // console.warn('SystemService > this.fcm.getToken()', token);

          this.SendTokenIfRetry(token).subscribe(() => {
            console.log('sendFcmToken success');
          });
        }, err => console.error(err));
      }

      this.user.loginSuccess$.pipe(
        filter(success => success)
      ).subscribe(() => {
        // console.warn('SystemService > this.user.loginSuccess$');
        getAndSendToken();
      });

      this.fcm.onTokenRefresh().subscribe(() => {
        console.log('SystemService > this.fcm.onTokenRefresh()');

        getAndSendToken();
      }, err => console.error(err));

      this.fcm.onBackgroundMessage().subscribe(payload => {
        // 푸시 수신 처리. 앱이 시작 안 했거나 BG에 있을 때
        console.log('SystemService > onBackgroundMessage()', payload);
        if (payload && 'page' in payload && typeof payload['page'] === 'string') {
          this.router.navigateByUrl(payload['page']).then();
        }
      }, err => console.error(err));

      this.fcm.onMessage().subscribe(payload => {
        console.log('SystemService > onMessage()', payload, this.cordovaSvc.platform);

        this.notiService.addNotification(payload);
      }, err => console.error(err));
    });


    /**************************************************************************
     * CUSTOM URL SCHEME 의 파라미터 받아오는 루틴
     */
    // override open handler to navigate on further custom url scheme actions
    (window as any).handleOpenURL = (url: string) => {
      // this context is called outside of angular zone!
      setTimeout(() => {
        // so we need to get back into the zone..
        this.zone.run(() => {
          // this is in the zone again..
          this.handleOpenUrl(url);
        });
      }, 0);
    };

    // check if app was opened by custom url scheme
    const lastUrl: string = (window as any).handleOpenURL_LastURL || '';
    if (lastUrl && lastUrl !== '') {
      delete (window as any).handleOpenURL_LastURL;
      this.handleOpenUrl(lastUrl);
    }

    // 저장된 소프트 업데이트 버전 정보 로드
    this.loadSoftUpdateVersion();
  }

  private handleOpenUrl(url: string): void {
    console.warn('handleOpenUrl url=', url);
    // custom url parsing, etc...

    if (url.startsWith(CUSTOM_URL_SCHEME + '://')) {
      const urlWithoutScheme = url.substring(CUSTOM_URL_SCHEME.length + 2);
      this.router.navigateByUrl(urlWithoutScheme);
    }
    else {
      console.error('handleOpenUrl() unknown url=', url);
    }
  }

  // private navigate(url): void {
  //   const [cmd, queryParams] = ParseRouteUrl(url);
  //
  //   console.log('startWith', cmd, queryParams);
  //   if (queryParams) {
  //     this.router.navigate([cmd], {queryParams}).then();
  //   }
  //   else {
  //     this.router.navigate([cmd]).then();
  //   }
  // }

  public GetServerInfo(): Observable<SERVER_INFO> {
    return this.api.Get(SYSTEM_INFO_API_URL);
  }

  /**
   * 서버 정보를 가져와서 버전을 체크하는 메서드
   * @param showAlert 버전 불일치 시 알림을 표시할 콜백 함수
   * @returns 서버 정보 Observable
   */
  public CheckAppVersion(
    showAlert: (title: string, message: string) => Observable<any>
  ): Observable<SERVER_INFO> {
    return this.GetServerInfo().pipe(
      tap((info: SERVER_INFO) => {
        console.log('server info =', info);

        // 플랫폼별 버전 가져오기
        const platformVersion = this.ios
          ? info.version.ios
          : info.version.android;
        const softVersion = platformVersion.soft;
        const hardVersion = platformVersion.hard;

        if (softVersion === APP_VERSION && hardVersion === APP_VERSION) {
          console.log('version check ok');
        } else if (CmpVersion(hardVersion, APP_VERSION) > 0) {
          const title = '앱을 새로 받아야 합니다.';
          const message = `현재 앱 버전: ${APP_VERSION}<br/>베풂에 접속하기 위한 최소 앱 버전: ${hardVersion}`
            + '<br/><br/>앱을 새로 받지 않으면 진행 할 수 없습니다.';
          this.showUpdatePopup(title, message, true).subscribe(data => console.log(data));
        } else if (CmpVersion(softVersion, APP_VERSION) > 0) {
          // 소프트 업데이트의 경우, 이전에 표시한 버전과 비교하고 날짜 확인
          const shouldShowPopup = this.shouldShowSoftUpdatePopup(softVersion);

          if (shouldShowPopup) {
            const title = '앱의 새 버전이 존재합니다.';
            const message = `현재 앱 버전: ${APP_VERSION}<br/>권장 앱 버전: ${softVersion}`;
            this.showUpdatePopup(title, message, false).subscribe(data => {
              console.log(data);
              // 팝업을 표시한 후 버전 정보 및 날짜 저장
              this.saveSoftUpdateVersion(softVersion);
            });
          } else {
            console.log('이미 표시된 소프트 업데이트 버전(또는 1주일 미만):', softVersion);
          }
        }
      })
    );
  }

  /**
   * 앱 업데이트 관련 팝업을 표시하는 메서드
   * @param title 팝업 제목
   * @param message 팝업 메시지
   * @param isRequired 필수 업데이트 여부
   * @returns 팝업 결과 Observable
   */
  private showUpdatePopup(title: string, message: string, isRequired: boolean): Observable<any> {
    return this.lm.OpenMsgPopup({
      title,
      msg: message,
      btnOk: '받으러 가기',
      btnCancel: isRequired ? null : '취소',
    }).pipe(
      tap((result: MP_RESULT) => {
        if (result === MP_RESULT.OK) {
          // 플러그인 없이 URL 스킴으로 앱 스토어 열기 시도
          this.openAppStore();
        }

        // 필수 업데이트인 경우, 사용자가 팝업창을 닫아도 다시 팝업 표시
        if (isRequired) {
          setTimeout(() => {
            this.showUpdatePopup(title, message, isRequired).subscribe();
          }, 500);
        }
      })
    );
  }

  private openAppStore(): void {
    try {
      const appStoreUrl = this.ios
        ? `itms-apps://itunes.apple.com/app/id${environment.iosAppId}`
        : `market://details?id=${environment.androidAppId}`;

      // URL 스킴을 직접 사용하여 앱 스토어 열기 시도
      window.location.href = appStoreUrl;

      // URL 스킴이 작동하지 않을 경우를 대비해 타이머 설정
      setTimeout(() => {
        // fallback: 브라우저에서 앱 스토어 웹페이지 열기
        this.openAppStoreInBrowser();
      }, 500);
    } catch (e) {
      console.error('앱 스토어 열기 실패:', e);
      // 오류 발생 시 fallback으로 브라우저에서 열기
      this.openAppStoreInBrowser();
    }
  }

  private openAppStoreInBrowser(): void {
    const appStoreUrl = this.ios
      ? `https://apps.apple.com/app/id${environment.iosAppId}`
      : `https://play.google.com/store/apps/details?id=${environment.androidAppId}`;

    // https://apps.apple.com/app/id6739878324

    if (this.cordovaSvc.cordova) {
      // Cordova 환경: 시스템 브라우저에서 열기
      const options = [
        'location=no',
        'fullscreen=yes',
        'toolbartranslucent=no',
        `toolbarcolor=${INAPPBROWSER_TOOLBAR_COLOR}`,
        `closebuttoncaption=${INAPPBROWSER_CLOSE_BUTTON_CAPTION}`,
        'hardwareback=yes',
        'hidenavigationbuttons=yes',
      ].join(',');

      (window as any).cordova.InAppBrowser.open(appStoreUrl, '_system', options);
    } else {
      // 웹 환경: 새 창으로 열기
      window.open(appStoreUrl, '_blank');
    }
  }

  public SendTokenIfRetry(token: string): Observable<void> {

    if (!device?.platform) {
      return new Observable<void>(observer => {
        observer.error('device or device.platform is null');
      });
    }

    // TODO: 개발 모드에서는 보내지 않는다. 개발 환경용 fcm 설정을 한 후에 이 코드를 풀어야 함.
    if (!environment.production) {
      return new Observable<void>(observer => {
        // observer.next();
        observer.complete();
      });
    }

    const sendFcmToken = (count: number) => {
      // console.warn('RetrySendToken() count =', count);

      return this.user.SendFcmToken(token,
        device.platform.toLowerCase() as PLATFORM_TYPE,
        device.model)
        .pipe(
          retryWhen(errors =>
            errors.pipe(
              mergeMap((error, index) => {
                const retryAttempt = index + 1;
                // 여기서 count 와 retryAttempt 를 비교하여 재시도 여부를 결정할 수 있습니다.
                if (retryAttempt > count) {
                  return throwError(() => new Error('Retry count exceeded'));
                }
                console.error(`Attempt ${retryAttempt}: retrying in ${retryAttempt * 1000}ms`);
                // 재시도하기 전에 대기시간 설정 (예: 1초 * 시도 횟수)
                return timer(retryAttempt * 1000);
              })
            )
          )
      );
    }

    return sendFcmToken(5);
  }

  /**
   * 소프트 업데이트 팝업을 표시해야 하는지 확인
   * @param newVersion 새 버전 문자열
   * @returns 팝업 표시 필요 여부
   */
  private shouldShowSoftUpdatePopup(newVersion: string): boolean {
    // 저장된 정보가 없는 경우
    if (!this.shownSoftUpdateVersion) {
      return true;
    }

    // 버전이 다른 경우 항상 표시
    if (this.shownSoftUpdateVersion.version !== newVersion) {
      return true;
    }

    // 버전이 같지만 1주일 이상 지난 경우 다시 표시
    const oneWeekInMillis = 7 * 24 * 60 * 60 * 1000; // 1주일을 밀리초로 계산
    const currentTime = Date.now();
    const lastShownTime = this.shownSoftUpdateVersion.date;
    const timeDiff = currentTime - lastShownTime;

    return timeDiff >= oneWeekInMillis;
  }

  /**
   * 소프트 업데이트 버전 정보를 로컬 스토리지에서 로드
   */
  private loadSoftUpdateVersion(): void {
    const savedInfo = localStorage.getItem(LOCALSTORAGE_KEY_SOFT_UPDATE);
    if (savedInfo) {
      try {
        this.shownSoftUpdateVersion = JSON.parse(savedInfo);
        console.log('저장된 소프트 업데이트 정보 로드:', this.shownSoftUpdateVersion);
      } catch (e) {
        console.error('소프트 업데이트 정보 파싱 오류:', e);
        this.shownSoftUpdateVersion = null;
      }
    }
  }

  /**
   * 소프트 업데이트 버전 정보를 로컬 스토리지에 저장
   * @param version 저장할 버전 문자열
   */
  private saveSoftUpdateVersion(version: string): void {
    const updateInfo: SoftUpdateInfo = {
      version: version,
      date: Date.now()
    };
    this.shownSoftUpdateVersion = updateInfo;
    localStorage.setItem(LOCALSTORAGE_KEY_SOFT_UPDATE, JSON.stringify(updateInfo));
    console.log('소프트 업데이트 정보 저장:', updateInfo);
  }
}
